import { Outlet, useParams } from 'react-router-dom';
import { AppBar, Logo } from '@acs/ui';
import { BusinessSwitcher } from '../features/businesses/index.js';
import { UserMenu } from './AppHeader.js';
import { useI18n } from '../i18n/index.js';

/**
 * Chrome for the setup wizard.
 *
 * Deliberately *not* the app shell: setup is one task at a time, so the section
 * nav would only be noise. What stays is the account menu and the business switcher
 * — so an owner with several businesses can jump to one that's already live, or
 * reach "Manage businesses".
 *
 * There is deliberately NO "Save & exit". Setup now locks the business until it
 * is finished (see the gate in `ActiveBusinessLayout`), so a button promising
 * an exit would either lie or fight the gate — the owner would press it and be
 * bounced straight back. Every step still saves as it goes; leaving is closing
 * the tab, and returning lands on the outstanding step.
 *
 * The business name stays in the bar so it is obvious WHICH business is being
 * set up — it is a label, not a switcher; jumping to a different business goes
 * through the account menu's "My businesses", and if that one is also
 * unfinished its own gate catches it. While the business doesn't exist yet
 * (creation, step 1) there is nothing to name, so it is absent and the step
 * itself offers Cancel.
 */
export function SetupLayout() {
  const { businessId } = useParams<{ businessId: string }>();
  const { t } = useI18n();

  return (
    <div className="app-shell">
      <AppBar
        start={
          <>
            {/* The full lock-up, phone included. The wizard is where a new
                owner meets the product, and its start slot has room the app
                shell's does not: no hamburger, no business name, and the title
                beside it drops out below 720px. */}
            <Logo className="appbar-brand appbar-brand--full" />
            <span className="setup-title">· {t('setup.title')}</span>
          </>
        }
        end={
          <>
            {/* No setup warning beside the name: this page is the setup. */}
            {businessId && <BusinessSwitcher setupMark={false} />}
            {/* The account menu rather than a bare theme toggle: the owner can
                still reach dark mode (it lives in the menu), and can also sign
                out or change language without finishing setup first. */}
            <UserMenu />
          </>
        }
      />
      {/* Same remount-on-switch rule as the app shell (see Layout): the wizard's
          steps seed their fields from the business doc at mount. `'new'` covers
          business creation, which has no business yet. */}
      <main className="page">
        <Outlet key={businessId ?? 'new'} />
      </main>
    </div>
  );
}
